import { createAction, createAsyncAction } from 'services/actionConfigs';
import {
  APPOINTMENT_LAYOUT,
  APPOINTMENT_STATUS,
  CALENDAR_ITEM_TYPES,
  CALENDAR_VIEW_TYPE,
  PREFIX_ACTIONS,
  PROLONGED_TIME_STATUS,
} from './constants';
import {
  IApiGetAppointments,
  IApiGetMultipleSettingBackground,
  IApiGetSettingBackground,
  IApiGetTableAppointment,
  IAppointmentMasterDataParams,
  IBodyApiUpdateAppointmentWithDrop,
} from './types/api';
import {
  IApiParamsGetSummaryByStatus,
  IAppointmentItemData,
  IAppointmentMasterDataResData,
  ISettingBackgroundItem,
} from './types/appointment';
import { IAppointmentState } from './types/reducer';
import {
  ICategoryItemData,
  IServiceItemData,
} from 'services/shop/types/categories';
import { ICalendarItemData } from './types/calendar';
import { ICustomer } from 'features/customer/services/types/api';
import { ISpecialServiceByStaffItem } from './adapters/service';

const getAppointments = createAsyncAction<IApiGetAppointments>(
  PREFIX_ACTIONS + 'getAppointments'
);

const getAppointmentMasterData = createAsyncAction<IAppointmentMasterDataParams, IAppointmentMasterDataResData>(
  PREFIX_ACTIONS + 'getAppointmentMasterData'
);
const getAppointmentMasterDataInitSuccess = createAction<{ date: string, total: number }>(
  PREFIX_ACTIONS + 'getAppointmentMasterDataInitSuccess'
);

const getLockBreakTimes = createAsyncAction<IApiGetAppointments>(
  PREFIX_ACTIONS + 'getLockBreakTimes'
);
const getLockBreakTimesForQuickBooking = createAsyncAction<IApiGetAppointments>(
  PREFIX_ACTIONS + 'getLockBreakTimesForQuickBooking'
);

const setParams = createAction<IApiGetAppointments | undefined>(
  PREFIX_ACTIONS + 'setParams'
);

const initData = createAction<string | undefined>(PREFIX_ACTIONS + '');
const initAptList = createAction(PREFIX_ACTIONS + 'initAptList');
const clearTableParams = createAction(PREFIX_ACTIONS + 'clearTableParams');
// UI ACTIONS
const setAppointmentLayout = createAction<APPOINTMENT_LAYOUT>(
  PREFIX_ACTIONS + 'setAppointmentLayout'
);
const setSelectedCalendarItemTypes = createAction<CALENDAR_ITEM_TYPES[]>(
  PREFIX_ACTIONS + 'setSelectedCalendarItemTypes'
);
const setCalendarViewType = createAction<CALENDAR_VIEW_TYPE>(
  PREFIX_ACTIONS + 'setCalendarViewType'
);
const setAppointmentDetail = createAction<IAppointmentItemData>(
  PREFIX_ACTIONS + 'setAppointmentDetail'
);
const decreaseStaffPage = createAction(PREFIX_ACTIONS + 'decreaseStaffPage');
const increaseStaffPage = createAction(PREFIX_ACTIONS + 'increaseStaffPage');
const setNewAppointmentDraftData = createAction<
  IAppointmentState['newAppointmentDraftData']
>(PREFIX_ACTIONS + 'setNewAppointmentDraftData');
const setNewBlockHourDraftData = createAction<IAppointmentState['newBlockHourDraftData']>(
  PREFIX_ACTIONS + 'setNewBlockHourDraftData'
);
const setNewBreakTimeDraftData = createAction<IAppointmentState['newBreakTimeDraftData']>(
  PREFIX_ACTIONS + 'setNewBreakTimeDraftData'
);
const setDraftAppointmentDetail = createAction<IAppointmentState['draftAppointmentData']>(
  PREFIX_ACTIONS + 'setDraftAppointmentDetail'
);

const addMoreTimeAppointment = createAction<{
  status: PROLONGED_TIME_STATUS;
  appointmentId: string;
}>(PREFIX_ACTIONS + 'addMoreTimeAppointment');

const updateAppointmentWithDrop =
  createAction<IBodyApiUpdateAppointmentWithDrop>(
    PREFIX_ACTIONS + 'updateAppointmentWithDrop'
  );

const deleteAppointment = createAsyncAction<string>(
  PREFIX_ACTIONS + 'deleteAppointment'
);

const updateCurrentAppointment = createAction<{ appointmentId: string, data: Partial<IAppointmentItemData> }>(
  PREFIX_ACTIONS + 'updateCurrentAppointment'
);

const addMoreBreakTime = createAction<string>(
  PREFIX_ACTIONS + 'addMoreBreakTime'
);
const minusBreakTime = createAction<string>(
  PREFIX_ACTIONS + 'minusBreakTime'
);

const setTableParams = createAction<IApiGetTableAppointment | undefined>(
  PREFIX_ACTIONS + 'setTableParams'
);

const getTableAppointments = createAsyncAction<IApiGetTableAppointment>(
  PREFIX_ACTIONS + 'getTableAppointments'
);

const setAppointmentStatusFilter = createAction<APPOINTMENT_STATUS[]>(
  PREFIX_ACTIONS + 'setAppointmentStatusFilter'
);

const setStaffLimit = createAction<number>(PREFIX_ACTIONS + 'setStaffLimit');

const removeBreakBlockHour = createAction<string>(
  PREFIX_ACTIONS + 'removeBreakBlockHour'
);

const NEW_APT_PREFIX = PREFIX_ACTIONS + 'newApt';
const newApt = {
  reset: createAction(NEW_APT_PREFIX + 'reset'),
  addService: createAction<IServiceItemData>(NEW_APT_PREFIX + 'addService'),
  removeService: createAction<IServiceItemData>(
    NEW_APT_PREFIX + 'removeService'
  ),
};
const ANYBODY_PREFIX = PREFIX_ACTIONS + 'anybodyBox';
const anybodyBox = {
  setOpen: createAction<boolean>(ANYBODY_PREFIX + 'setOpen'),
  setAnybodyAppointmentList: createAction<ICalendarItemData[]>(
    ANYBODY_PREFIX + 'setAnybodyAppointmentList'
  ),
};

const getListStaffAvailableBooking = createAsyncAction<string>(
  PREFIX_ACTIONS + 'getListStaffAvailableBooking'
);

const getListStaffsOff = createAsyncAction<IApiGetAppointments>(
  PREFIX_ACTIONS + 'getListStaffsOff'
);

const getServicesByStaff = createAsyncAction<string, ICategoryItemData[]>(
  PREFIX_ACTIONS + 'getServicesByStaff'
);
const getServicesByStaffQuickBook = createAsyncAction<
  string,
  ISpecialServiceByStaffItem,
  ISpecialServiceByStaffItem
>(PREFIX_ACTIONS + 'getServicesByStaffQuickBook');

const addAnybody = createAction(PREFIX_ACTIONS + 'addAnybody');
const removeAnybody = createAction<string>(PREFIX_ACTIONS + 'removeAnybody');

const getSettingBackground = createAsyncAction<void>(
  PREFIX_ACTIONS + 'getSettingBackground'
);
const updateSettingBackground = createAsyncAction<
  IApiGetSettingBackground,
  ISettingBackgroundItem
>(PREFIX_ACTIONS + 'updateSettingBackground');

const updateMultipleSettingBackground = createAsyncAction<IApiGetMultipleSettingBackground>(PREFIX_ACTIONS + 'updateMultipleSettingBackground');

const syncCustomerInfo = createAction<ICustomer>(
  PREFIX_ACTIONS + 'syncCustomerInfo'
);

const setStaffFilterIds = createAction<string[]>(PREFIX_ACTIONS + 'setStaffFilterIds');

const summaryByStatus = createAsyncAction<IApiParamsGetSummaryByStatus>(PREFIX_ACTIONS + 'summaryByStatus');

const updateColorAppointment = createAction<{ appointmentId: string; color: string }>(PREFIX_ACTIONS + 'updateColorAppointment');

const appointmentActions = {
  newApt,

  setTableParams,
  getTableAppointments,

  updateCurrentAppointment,

  setParams,
  getAppointments,
  getAppointmentMasterData,
  getAppointmentMasterDataInitSuccess,
  getLockBreakTimes,
  getLockBreakTimesForQuickBooking,
  initData,
  initAptList,
  clearTableParams,
  decreaseStaffPage,
  increaseStaffPage,
  setNewAppointmentDraftData,
  setNewBlockHourDraftData,
  setNewBreakTimeDraftData,
  //
  setAppointmentLayout,
  setSelectedCalendarItemTypes,
  setCalendarViewType,
  setAppointmentDetail,
  setDraftAppointmentDetail,
  addMoreTimeAppointment,
  addMoreBreakTime,
  minusBreakTime,
  updateAppointmentWithDrop,
  deleteAppointment,
  setAppointmentStatusFilter,
  removeBreakBlockHour,
  setStaffLimit,
  anybodyBox,
  getListStaffAvailableBooking,
  getListStaffsOff,
  getServicesByStaff,
  getServicesByStaffQuickBook,

  addAnybody,
  removeAnybody,
  getSettingBackground,
  updateSettingBackground,
  updateMultipleSettingBackground,
  syncCustomerInfo,
  setStaffFilterIds,
  summaryByStatus,

  updateColorAppointment,
};

export default appointmentActions;
